import classNames from 'classnames';
import React, { useState, useEffect, useMemo } from 'react';
import styles from './style.module.scss';
import { PlusIcon, ProjectNameIcon, StatusCheckIcon, EditIcon, DelIcon, EyeIcon, LinkIcon, CalendarIcon } from 'assets';
import { useSelector } from 'react-redux';
import GenericTable from 'shared/components/common/table/table';
import Heading from 'shared/components/common/heading/Heading';
import CustomButton from 'shared/components/common/custom_button/CustomButton';
import DashboardWrapper from 'shared/components/common/dashboard_wrapper/DashboardWrapper';
import AddQRCodeModal from 'shared/modal/qr_code/AddQRCodeModal';
import ViewQRCodeModal from 'shared/modal/qr_code/ViewQRCodeModal';
import {
    GetAllQRCodesQuery,
    DeleteQRCodeMutation,
    UpdateQRCodeMutation
} from 'shared/services/qr_code_service/QRCodeService';
import OverlayLoading from 'shared/screen_loader/overlay_loader/OverlayLoading';
import DeleteConfirmation from 'shared/modal/delete_confirmation/DeleteConfirmation';
import SearchBar from 'shared/components/common/searchbar/SearchBar';
import { toastMessage } from 'shared/components/toast/Toast';
import { BaseURL } from 'shared/utils/Endpoints';
import moment from 'moment';

export default function QRCodeManagement() {
    const {
        theme: { theme }
    } = useSelector((state) => state.root);

    // Mutations & Queries
    const { mutate: deleteMutate, isLoading: deleteLoading, isSuccess: deleteSuccess } = DeleteQRCodeMutation();
    const { mutate: updateMutate, isLoading: updateLoading } = UpdateQRCodeMutation();
    const { isLoading: queryLoading, data: listData } = GetAllQRCodesQuery();

    const [searchResults, setSearchResults] = useState([]);

    // Modal states
    const [openAddModal, setOpenAddModal] = useState(false);
    const [openViewModal, setOpenViewModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [openDelConfirm, setOpenDelConfirm] = useState(false);

    useEffect(() => {
        if (listData) {
            setSearchResults(listData);
            if (selectedItem) {
                const refreshed = listData.find((i) => i._id === selectedItem._id);
                if (refreshed) setSelectedItem(refreshed);
            }
        }
    }, [listData]);

    useEffect(() => {
        if (deleteSuccess) {
            setSelectedItem(null);
            setOpenDelConfirm(false);
        }
    }, [deleteSuccess]);

    const handleCopyRedirect = (code) => {
        const backendOrigin = BaseURL.includes('localhost') ? 'https://api.wisetechshop.com' : BaseURL.replace(/\/api\/?$/, '');
        const redirectUrl = `${backendOrigin}/qr/${code}`;
        navigator.clipboard.writeText(redirectUrl);
        toastMessage('success', 'Redirect Link copied to clipboard!');
    };

    const handleToggleStatus = (item) => {
        updateMutate({
            id: item._id,
            payload: {
                is_active: !item.is_active
            }
        });
    };

    const columns = useMemo(
        () => [
            {
                Header: (
                    <div className={classNames(`${theme}FontColor w-100`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Title / Name
                    </div>
                ),
                accessor: 'title',
                Cell: ({ row }) => (
                    <div className="w-100">
                        <div className="fw-bold">{row.original.title}</div>
                        {row.original.description && (
                            <div className="text-muted small" style={{ fontSize: '0.8rem' }}>
                                {row.original.description}
                            </div>
                        )}
                    </div>
                )
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor w-100`)}>
                        <LinkIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Short Code
                    </div>
                ),
                accessor: 'code',
                Cell: ({ row }) => (
                    <div className="d-flex align-items-center gap-2">
                        <span className={styles.codeBadge}>{row.original.code}</span>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary py-0 px-1"
                            title="Copy Short Redirect Link"
                            onClick={() => handleCopyRedirect(row.original.code)}
                            style={{ fontSize: '11px' }}
                        >
                            📋 Copy
                        </button>
                    </div>
                )
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor w-100`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Type
                    </div>
                ),
                accessor: 'qr_type',
                Cell: ({ row }) => {
                    const isStatic = row.original.qr_type === 'static';
                    return (
                        <span className={classNames('badge', isStatic ? 'bg-success' : 'bg-primary')}>
                            {isStatic ? '🎯 Static (Direct)' : '⚡ Dynamic'}
                        </span>
                    );
                }
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor w-100`)}>
                        <LinkIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Target URL
                    </div>
                ),
                accessor: 'target_url',
                Cell: ({ row }) => {
                    const url = row.original.target_url;
                    const fullUrl = url.startsWith('http') ? url : `https://${url}`;
                    return (
                        <a
                            href={fullUrl}
                            target="_blank"
                            rel="noreferrer"
                            className={styles.targetUrlLink}
                            title={url}
                        >
                            {url}
                        </a>
                    );
                }
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor w-100`)}>
                        <StatusCheckIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Scans
                    </div>
                ),
                accessor: 'scan_count',
                Cell: ({ row }) => (
                    <div className="w-100">
                        {row.original.qr_type === 'static' ? (
                            <span className="text-muted small">Direct</span>
                        ) : (
                            <span className="badge bg-secondary">{row.original.scan_count || 0}</span>
                        )}
                    </div>
                )
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor w-100`)}>
                        <StatusCheckIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Status
                    </div>
                ),
                accessor: 'is_active',
                Cell: ({ row }) => (
                    <div className="form-check form-switch d-flex align-items-center">
                        <input
                            className="form-check-input pointer"
                            type="checkbox"
                            role="switch"
                            checked={row.original.is_active}
                            onChange={() => handleToggleStatus(row.original)}
                            style={{ width: '2.2rem', height: '1.2rem', cursor: 'pointer' }}
                        />
                        <span className={classNames('ms-2 small', row.original.is_active ? 'text-success' : 'text-danger')}>
                            {row.original.is_active ? 'Active' : 'Disabled'}
                        </span>
                    </div>
                )
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor w-100`)}>
                        <CalendarIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Created
                    </div>
                ),
                accessor: 'created_at',
                Cell: ({ row }) => (
                    <div className="w-100 small text-muted">
                        {row.original.created_at ? moment(row.original.created_at).format('DD MMM YYYY, hh:mm A') : '-'}
                    </div>
                )
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor w-100 text-end me-2`)}>
                        <StatusCheckIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Action
                    </div>
                ),
                accessor: 'action',
                Cell: ({ row }) => (
                    <div className="d-flex justify-content-end align-items-center gap-2 me-2">
                        <EyeIcon
                            className="pointer"
                            title="View & Download QR Code"
                            style={{ width: '18px', height: '18px', color: '#3b82f6' }}
                            onClick={() => {
                                setSelectedItem(row.original);
                                setOpenViewModal(true);
                            }}
                        />
                        <EditIcon
                            className="pointer"
                            title="Edit Target URL or Details"
                            style={{ width: '18px', height: '18px' }}
                            onClick={() => {
                                setSelectedItem(row.original);
                                setIsEdit(true);
                                setOpenAddModal(true);
                            }}
                        />
                        <DelIcon
                            className="pointer"
                            title="Delete QR Code"
                            onClick={() => {
                                setSelectedItem(row.original);
                                setOpenDelConfirm(true);
                            }}
                        />
                    </div>
                )
            }
        ],
        [theme]
    );

    return (
        <DashboardWrapper>
            <div className={classNames(styles.UserContainer, 'text-start')}>
                <SearchBar
                    searchKey="title"
                    suggestionsSource={listData}
                    onSearchChange={setSearchResults}
                />
                <div className={styles.Header}>
                    <div>
                        <Heading heading={'Dynamic QR Code Management'} headingStyle={styles.Heading} />
                        <div className="text-muted small mt-1">
                            Generate permanent QR codes with customizable destination URLs. Change destinations anytime without re-printing!
                        </div>
                    </div>
                    <div className="d-flex">
                        <CustomButton
                            Icon={PlusIcon}
                            IconStyle={classNames('me-1', styles.IconStyle)}
                            text={'New QR Code'}
                            submitHandle={() => {
                                setIsEdit(false);
                                setSelectedItem(null);
                                setOpenAddModal(true);
                            }}
                            containerStyle={classNames(styles.AddProduct)}
                        />
                    </div>
                </div>

                <GenericTable
                    columns={columns}
                    data={searchResults || []}
                    TDTextStyle={classNames(styles.TDTextStyle)}
                    TableWrapperStyle={classNames(styles.Table)}
                />

                <AddQRCodeModal
                    show={openAddModal}
                    handleClose={() => setOpenAddModal(false)}
                    isEdit={isEdit}
                    selectedItem={selectedItem}
                />

                <ViewQRCodeModal
                    show={openViewModal}
                    handleClose={() => setOpenViewModal(false)}
                    selectedItem={selectedItem}
                />

                <DeleteConfirmation
                    show={openDelConfirm}
                    loading={deleteLoading}
                    ConfimationHeading="Delete QR Code"
                    Desc={'Are you sure you want to delete this QR Code? Any printed copies will stop redirecting.'}
                    DeleteHandler={() => {
                        if (selectedItem) deleteMutate(selectedItem._id);
                    }}
                    handleClose={() => setOpenDelConfirm(false)}
                />

                {(queryLoading || updateLoading) && <OverlayLoading />}
            </div>
        </DashboardWrapper>
    );
}
